import { serialize } from 'object-to-formdata'
import { SimpleResponse } from 'Types/simpleResponse'
import { RequestParams, ResponseCatalog } from 'Models'
import { OutLinks } from 'Src/constants/OutLinks'
import { axiosDS } from '@pp/axios'
import { urls } from 'Src/constants/Urls'
import {
	OrderModel,
	CommentModel,
	ContactDetails,
	OrderBuildRequest,
	OrderStatus,
	Status,
	ReportType,
	UnsignedOrdersModel,
	getUrlForResendEmail,
	getUrlForResendSms,
	getUrlOrdersReport,
	ReserveAccountStatus,
} from 'Pages/orders_design'
import { ProductSlugs } from 'Src/constants/ProductSlugs'
import { FileWithPreview } from 'LibNeo'

async function getAll(params?: RequestParams) {
	const res = await axiosDS().get<ResponseCatalog<OrderModel>>(urls.orders.orders, { params })
	return res.data
}

async function getTotal(params?: RequestParams) {
	const res = await axiosDS().get<{ count: number }>(urls.orders.total_orders, { params })
	return res.data
}

export async function getStatuses(params?: RequestParams) {
	const res = await axiosDS().get<Status[]>(urls.orders.orderStatuses, { params })
	return res.data
}

export async function sendOrdersReport(type: ReportType, params?: RequestParams) {
	const res = await axiosDS().get<SimpleResponse>(getUrlOrdersReport(type), { params })
	return res.data
}

export async function sendPaymentStatuses(file: FileWithPreview | FileWithPreview[]) {
	const data = new FormData()
	// @ts-ignore
	data.append('[data][file]', file)
	const res = await axiosDS().patch<SimpleResponse>(urls.orders.sendStatuses, data)
	return res.data
}

export async function sendCorusStatuses(file: FileWithPreview | FileWithPreview[]) {
	const data = new FormData()
	// @ts-ignore
	data.append('[data][file]', file)
	const res = await axiosDS().patch<SimpleResponse>(urls.orders.product_order_status, data)
	return res.data
}
async function sendCRMStatuses(file: FileWithPreview | FileWithPreview[]) {
	const data = new FormData()
	// @ts-ignore
	data.append('[data][file]', file)
	const res = await axiosDS().patch<SimpleResponse>(urls.orders.crm_status, data)
	return res.data
}

async function getOrder(id: number) {
	const res = await axiosDS().get<OrderModel>(`${urls.orders.orders}/${id}`)
	return res.data
}

async function sendComment(id: number, text: string) {
	const res = await axiosDS().post<CommentModel>(
		urls.orders.comment.replace(':id', id.toString()),
		{
			data: { text },
		}
	)
	return res.data
}
async function buildOrder({
	inn,
	accept_blocked,
	products: { product_ids, online_product_ids },
}: OrderBuildRequest) {
	const res = await axiosDS().post<OrderModel>(`${urls.orders.orders}/build`, {
		data: { inn, product_ids, accept_blocked, online_product_ids },
	})
	return res.data
}

async function getTotalUncompletedOnline() {
	const res = await axiosDS().get<{ count: number }>(urls.orders.total_orders, {
		params: {
			incompleted: true,
			online: true,
		},
	})
	return res.data
}

async function getBlockExpiresSoon() {
	const res = await axiosDS().get<{ count: number }>(urls.orders.total_orders, {
		params: {
			block_expires_soon: true,
		},
	})
	return res.data
}

async function createOrder(data: OrderModel) {
	const formData = serialize({ data }, { allowEmptyArrays: true })
	const res = await axiosDS().post<OrderModel>(
		`${urls.orders.orders}?without_rbidos_type=true`,
		formData,
		{
			headers: {
				'content-type': 'multipart/form-data',
			},
		}
	)
	return res.data
}

async function getTotalUnblockChecking() {
	const res = await axiosDS().get<{ count: number }>(urls.orders.total_orders, {
		params: {
			status_name: OrderStatus.UNBLOCK_CLARIFICATION,
		},
	})
	return res.data
}

async function getTotalPartnerRequests() {
	const res = await axiosDS().get<{ count: number }>(urls.orders.total_orders, {
		params: {
			with_partner_request: true,
		},
	})
	return res.data
}

async function deleteOrder(id: number) {
	const res = await axiosDS().delete<SimpleResponse>(`${urls.orders.orders}/${id}`)
	return res.data
}

async function getContactDetailsOrder(id: number) {
	const res = await axiosDS().get<ContactDetails>(
		urls.orders.contact_details.replace(':id', id.toString())
	)
	return res.data
}

async function resendSms(id: number, productSlug: ProductSlugs) {
	const res = await axiosDS().post<SimpleResponse>(
		getUrlForResendSms(productSlug).replace(':id', id.toString())
	)
	return res.data
}

async function resendEmail(id: number, productSlug: ProductSlugs) {
	const res = await axiosDS().post<SimpleResponse>(
		getUrlForResendEmail(productSlug).replace(':id', id.toString())
	)
	return res.data
}

async function getIntegrationOrder(id: number) {
	const res = await axiosDS().get<OrderModel>(
		urls.orders.get_integration.replace(':id', id.toString()),
		{
			params: { integration_channel: 'sber_telecom' },
		}
	)
	return res.data
}

async function sendClickThrough() {
	const res = await axiosDS().post<SimpleResponse>(urls.statistics.create, {
		data: {
			statistic_type: 1,
			url: OutLinks.tariffsSber,
		},
	})
	return res.data
}

async function getUnsignedOrders() {
	const res = await axiosDS().get<UnsignedOrdersModel>(urls.orders.not_signed)
	return res.data
}

async function getOtkritieBankCities() {
	const res = await axiosDS().get<{ cities: string[] }>(urls.orders.otkritie_bank_cities)
	return res.data
}

async function getTotalTasks() {
	const res = await axiosDS().get<{ count: number }>(urls.orders.total_orders, {
		params: {
			with_incompleted_tasks: true,
		},
	})
	return res.data
}

async function dzoStatusApi(params: { event_id: number }) {
	const res = await axiosDS().post(urls.orders.dzo, { data: params })
	return res.data
}
async function paoSberbankStatusApi(params: { event_id: number }) {
	const res = await axiosDS().post(urls.orders.pao_sberbank, { data: params })
	return res.data
}
async function crmStatusesStatusApi(params: { event_id: number }) {
	const res = await axiosDS().post(urls.orders.crm_statuses, { data: params })
	return res.data
}

async function recognizePassport(params: { file: Blob; order_id: string }) {
	const data = new FormData()
	data.append('data[file]', params.file)
	data.append('data[order_id]', params.order_id)
	const res = await axiosDS().post(urls.orders.recognize_passport, data)
	return res.data
}
export async function bankAccountStatuses(file: FileWithPreview | FileWithPreview[]) {
	const data = new FormData()
	data.append('[data][file]', file as unknown as File)
	const res = await axiosDS().patch<SimpleResponse>(urls.orders.bankAccountStatuses, data)
	return res.data
}

async function getTotalRejectedReservation() {
	const res = await axiosDS().get<{ count: number }>(urls.orders.total_orders, {
		params: {
			reserve_account_status: ReserveAccountStatus.rejected_reservation,
		},
	})
	return res.data
}

export const orderApi = {
	getAll,
	getTotal,
	getStatuses,
	getOrder,
	sendComment,
	deleteOrder,
	getContactDetailsOrder,
	resendSms,
	resendEmail,
	getIntegrationOrder,
	sendOrdersReport,
	sendPaymentStatuses,
	sendCorusStatuses,
	sendCRMStatuses,
	buildOrder,
	createOrder,
	getTotalUncompletedOnline,
	getTotalUnblockChecking,
	getTotalPartnerRequests,
	sendClickThrough,
	getUnsignedOrders,
	getOtkritieBankCities,
	getTotalTasks,
	dzoStatusApi,
	paoSberbankStatusApi,
	crmStatusesStatusApi,
	recognizePassport,
	getBlockExpiresSoon,
	getTotalRejectedReservation,
	bankAccountStatuses,
}
